import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { 
  MdBusinessCenter, MdEmail, MdPhone, 
  MdLocationOn, MdImage, MdCheckCircle, MdNavigateNext, MdNavigateBefore,
  MdStorefront, MdInfo
} from "react-icons/md";

// Firebase Imports
import { db, storage, auth } from "./firebase";
// Added setDoc and doc for fixed ID saving
import { doc, setDoc, serverTimestamp } from "firebase/firestore"; 
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";

// --- Styled Components (Avaami Theme) ---
const Container = styled.div`
  background-color: #f0f2f5;
  min-height: 100vh;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (min-width: 768px) {
    padding: 40px 20px;
  }
`;

const FormCard = styled.div`
  background: white;
  width: 100%;
  max-width: 850px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StepperWrapper = styled.div`
  background: #fff;
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  position: relative;

  @media (min-width: 768px) {
    padding: 30px 40px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 40px;
    left: 60px;
    right: 60px;
    height: 2px;
    background: #e0e0e0;
    z-index: 0;

    @media (max-width: 480px) {
        display: none;
    }
  }
`;

const StepItem = styled.div`
  position: relative;
  z-index: 1;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
`;

const StepCircle = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: ${props => props.active ? '#1a73e8' : '#fff'};
  border: 2px solid ${props => props.active || props.completed ? '#1a73e8' : '#ccc'};
  color: ${props => props.active ? '#fff' : '#777'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
  transition: 0.3s;
  
  ${props => props.completed && `
    background: #1a73e8;
    color: white;
  `}
`;

const StepLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.active ? '#1a73e8' : '#888'};
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

const FormContent = styled.div`
  padding: 20px;
  min-height: 350px;

  @media (min-width: 768px) {
    padding: 40px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  color: #1a202c;
  margin-bottom: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`margin-bottom: 18px;`;
const Label = styled.label`display: block; font-weight: 600; margin-bottom: 6px; color: #4a5568; font-size: 14px;`;
const Input = styled.input`
  width: 100%; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 15px; box-sizing: border-box;
  &:focus { outline: none; border-color: #1a73e8; box-shadow: 0 0 0 3px rgba(26,115,232,0.1); }
`;
const TextArea = styled.textarea`
  width: 100%; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 15px; min-height: 100px; box-sizing: border-box;
  &:focus { outline: none; border-color: #1a73e8; box-shadow: 0 0 0 3px rgba(26,115,232,0.1); }
`;

const UploadBox = styled.label`
  border: 2px dashed #cbd5e0; border-radius: 12px; padding: 25px; text-align: center; cursor: pointer;
  background: #f8fafc; transition: 0.2s; display: flex; flex-direction: column; align-items: center;
  &:hover { border-color: #1a73e8; background: #f0f7ff; }
`;
const PreviewImg = styled.img`width: 100%; height: 160px; object-fit: cover; border-radius: 10px; margin-top: 10px;`;

const ReviewGrid = styled.div`
  background: #f8fafc; padding: 20px; border-radius: 12px; border: 1px solid #e2e8f0;
  display: grid; grid-template-columns: 1fr 1fr; gap: 15px;
`;
const ReviewItem = styled.div`
  h4 { margin: 0 0 4px; font-size: 12px; color: #718096; text-transform: uppercase; letter-spacing: 0.5px; }
  p { margin: 0; font-size: 15px; font-weight: 600; color: #2d3748; }
`;

const ButtonGroup = styled.div`
  padding: 15px 20px; border-top: 1px solid #eee; display: flex; justify-content: space-between; background: #fff;
  
  @media (min-width: 768px) {
    padding: 20px 40px;
  }
`;

const Button = styled.button`
  padding: 10px 20px; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; border: none;
  display: flex; align-items: center; gap: 8px; transition: 0.2s;
  background: ${props => props.primary ? '#1a73e8' : '#edf2f7'};
  color: ${props => props.primary ? '#fff' : '#4a5568'};
  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { background: #cbd5e0; cursor: not-allowed; }
`;

const CreateBusinessPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({
    businessName: "", category: "School", tagline: "", description: "", establishedYear: "",
    email: "", phone: "", website: "", location: "",
    facebook: "", instagram: "", linkedin: ""
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (u) {
        setUser(u);
      } else {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) { 
      setFile(file); 
      setPreview(URL.createObjectURL(file)); 
    }
  };

  const validateStep = () => {
    if (activeStep === 0) {
      if (!formData.businessName || !formData.description) return alert("Please fill Business Name and Description.");
    }
    if (activeStep === 1) {
      if (!formData.email || !formData.phone) return alert("Email and Phone Number are required.");
    }
    setActiveStep(prev => prev + 1);
  };

  const uploadFile = async (file, path) => {
    if (!file) return null;
    const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    await new Promise((resolve, reject) => uploadTask.on("state_changed", null, reject, resolve));
    return await getDownloadURL(uploadTask.snapshot.ref);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const logoUrl = await uploadFile(logoFile, "avaami_business_logos");
      const coverImageUrl = await uploadFile(coverFile, "avaami_business_covers");

      // FIX: Use setDoc with user.uid as the document ID
      // This ensures Home.js can find the profile using the user's UID
      await setDoc(doc(db, "businessPages", user.uid), {
        ...formData,
        logoUrl: logoUrl || null,
        coverImageUrl: coverImageUrl || null,
        ownerUid: user.uid, // Field for querying
        createdAt: serverTimestamp(),
        followersCount: 0,
        verified: false,
        platform: "Avaami Business Connect"
      });

      alert("🎉 Business Page Created Successfully!");
      navigate("/Home"); // Navigate to Home to see the button change
    } catch (error) {
      console.error(error);
      alert("Error creating page.");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { label: "Institution Info", icon: <MdStorefront /> },
    { label: "Contact Info", icon: <MdEmail /> },
    { label: "Branding", icon: <MdImage /> },
    { label: "Review & Submit", icon: <MdCheckCircle /> },
  ];

  return (
    <Container>
      <FormCard>
        <StepperWrapper>
          {steps.map((step, index) => (
            <StepItem key={index}>
              <StepCircle active={index === activeStep} completed={index < activeStep}>
                {index < activeStep ? <MdCheckCircle /> : index + 1}
              </StepCircle>
              <StepLabel active={index === activeStep}>{step.label}</StepLabel>
            </StepItem>
          ))}
        </StepperWrapper>

        <FormContent>
          {activeStep === 0 && (
            <>
              <SectionTitle><MdBusinessCenter color="#1a73e8"/> Business Details</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Business Name</Label>
                  <Input name="businessName" value={formData.businessName} onChange={handleChange} placeholder="e.g. Green Valley School" />
                </FormGroup>
              </Grid>
              <FormGroup>
                <Label>Tagline</Label>
                <Input name="tagline" value={formData.tagline} onChange={handleChange} placeholder="e.g. Empowering Future Leaders" />
              </FormGroup>
              <FormGroup>
                <Label>About Your Business </Label>
                <TextArea name="description" value={formData.description} onChange={handleChange} placeholder="Describe your services and objectives..." />
              </FormGroup>
            </>
          )}

          {activeStep === 1 && (
            <>
              <SectionTitle><MdPhone color="#1a73e8"/> Contact Details</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="contact@institution.com" />
                </FormGroup>
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXXXXXXX" />
                </FormGroup>
              </Grid>
              <Grid>
                <FormGroup>
                  <Label>Website</Label>
                  <Input name="website" value={formData.website} onChange={handleChange} placeholder="https://www.example.com" />
                </FormGroup>
                <FormGroup>
                  <Label>Location</Label>
                  <Input name="location" value={formData.location} onChange={handleChange} placeholder="City, State" />
                </FormGroup>
              </Grid>
            </>
          )}

          {activeStep === 2 && (
            <>
              <SectionTitle><MdImage color="#1a73e8"/> Branding & Visuals</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Business Logo</Label>
                  <UploadBox>
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setLogoFile, setLogoPreview)} />
                    {logoPreview ? <PreviewImg src={logoPreview} style={{height: 100, width: 100, borderRadius: '50%'}} /> : <MdStorefront size={40} color="#cbd5e0" />}
                    <span style={{marginTop: 10, color: '#718096', fontSize: '13px'}}>Click to upload Logo</span>
                  </UploadBox>
                </FormGroup>
                <FormGroup>
                  <Label>Cover Image</Label>
                  <UploadBox>
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setCoverFile, setCoverPreview)} />
                    {coverPreview ? <PreviewImg src={coverPreview} /> : <MdImage size={40} color="#cbd5e0" />}
                    <span style={{marginTop: 10, color: '#718096', fontSize: '13px'}}>Click to upload Cover</span>
                  </UploadBox>
                </FormGroup>
              </Grid>
            </>
          )}

          {activeStep === 3 && (
            <>
              <SectionTitle><MdCheckCircle color="#28a745"/> Review Details</SectionTitle>
              <ReviewGrid>
                <ReviewItem><h4>Business Name</h4><p>{formData.businessName}</p></ReviewItem>
                <ReviewItem><h4>Email</h4><p>{formData.email}</p></ReviewItem>
                <ReviewItem><h4>Phone</h4><p>{formData.phone || "N/A"}</p></ReviewItem>
                <ReviewItem><h4>Location</h4><p>{formData.location || "N/A"}</p></ReviewItem>
              </ReviewGrid>
            </>
          )}
        </FormContent>

        <ButtonGroup>
          {activeStep > 0 ? (
            <Button onClick={() => setActiveStep(prev => prev - 1)}>
              <MdNavigateBefore size={20}/> Back
            </Button>
          ) : (
            <Button onClick={() => navigate("/")}>Cancel</Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button primary onClick={validateStep}>
              Next <MdNavigateNext size={20}/>
            </Button>
          ) : (
            <Button primary onClick={handleSubmit} disabled={loading}>
              {loading ? "Submitting..." : "Submit Page"} <MdCheckCircle size={20}/>
            </Button>
          )}
        </ButtonGroup>
      </FormCard>
    </Container>
  );
};

export default CreateBusinessPage;